import type { Product } from "schemas/generated/product";

import * as React from "react";

import PageHeader from "@replo/design-system/blocks/page-header/PageHeader";
import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { Input } from "@replo/design-system/components/input/Input";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@replo/design-system/components/table/Table";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { ProductTypeIcon } from "@v2/features/products/components/ProductTypeIcon";
import { PRODUCT_TYPE_OPTIONS } from "@v2/features/products/constants/product-type-options";
import { useAllowScroll } from "@v2/features/products/hooks/useAllowScroll";
import { StatusIcon } from "@v2/features/products/StatusIcon";
import {
  getPriceRange,
  getStatusBadgeClasses,
  openShopifyProductPageUrl,
} from "@v2/features/products/utils";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { productRouter } from "@v2/trpc/routes/product";
import { routes } from "@v2/utils/router";
import startCase from "lodash-es/startCase";
import {
  ExternalLink,
  MoreHorizontal,
  Package,
  Plus,
  Search,
} from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

const ProductMenu: React.FC<{ onEditProduct: () => void }> = ({
  onEditProduct,
}) => {
  return (
    <Menu>
      <MenuTrigger
        render={
          <Button
            icon
            variant="tertiary"
            size="sm"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="text-muted" />
          </Button>
        }
      />

      <MenuContent align="end">
        <MenuItem label="Edit Product" onClick={onEditProduct} />
      </MenuContent>
    </Menu>
  );
};

const PAGE_SIZE = 20;

type ProductTableRow =
  | (Product & { key: string; isLoading?: false })
  | { key: string; isLoading: true };

const ShopifyProductCells: React.FC<{ product: Product }> = ({ product }) => {
  const variantCount = product.variants?.length ?? 0;
  const variantTitles =
    product.variants?.map((variant) => variant.title ?? "Untitled variant") ??
    [];
  const visibleTitles = variantTitles.slice(0, 5);
  const remaining = variantTitles.length - 5;
  const variantTooltipContent =
    remaining > 0
      ? `${visibleTitles.join(", ")}... and ${remaining} more`
      : visibleTitles.join(", ");

  const optionCount = product.options?.length ?? 0;
  const optionsList =
    optionCount > 0
      ? product.options
          ?.map((option) => {
            const values =
              option.optionValues?.map((ov) => ov.value).join(", ") ??
              "No values";
            return `${option.title}: ${values}`;
          })
          .join(", ") ?? ""
      : "No options";

  return (
    <>
      <TableCell>
        <div className="flex items-center gap-3 min-w-0">
          {product.featuredMedia?.url ? (
            <img
              src={product.featuredMedia.url}
              alt={product.title}
              className="h-12 w-12 rounded object-cover flex-shrink-0"
            />
          ) : (
            <div className="bg-sheet text-muted flex h-12 w-12 items-center justify-center rounded flex-shrink-0">
              <Package className="h-6 w-6" />
            </div>
          )}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <StatusIcon status={product.status} />
            <div className="flex flex-col min-w-0 flex-1">
              <div className="text-default cursor-pointer text-base font-medium truncate">
                {product.title}
              </div>
              <div className="text-muted text-xs truncate">
                {product.slug ?? "N/A"}
              </div>
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="text-muted text-xs">
          {getPriceRange(product.variants)}
        </div>
      </TableCell>
      <TableCell>
        <div
          className={twMerge(
            "inline-block rounded-full px-2 py-0.5 text-2xs font-medium",
            getStatusBadgeClasses(product.status),
          )}
        >
          {startCase(product.status)}
        </div>
      </TableCell>
      <TableCell>
        <div className="text-muted text-xs truncate">
          {product.vendor ?? "N/A"}
        </div>
      </TableCell>
      <TableCell>
        <Tooltip content={variantTooltipContent} triggerAsChild side="top">
          <span className="text-muted text-xs cursor-pointer hover:text-foreground">
            {variantCount} {variantCount === 1 ? "variant" : "variants"}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Tooltip content={optionsList} triggerAsChild side="top">
          <span className="text-muted text-xs cursor-pointer hover:text-foreground">
            {optionCount} {optionCount === 1 ? "option" : "options"}
          </span>
        </Tooltip>
      </TableCell>
    </>
  );
};

const ReploProductCells: React.FC<{
  product: Product;
  onEditProduct: () => void;
}> = ({ product, onEditProduct }) => {
  const productTypeOption = PRODUCT_TYPE_OPTIONS.find(
    (option) => option.value === product.type,
  );

  return (
    <>
      <TableCell>
        <div className="flex items-center gap-3 min-w-0">
          {product.featuredMedia?.url ? (
            <img
              src={product.featuredMedia.url}
              alt={product.title}
              className="h-12 w-12 rounded object-cover flex-shrink-0"
            />
          ) : (
            <div className="bg-sheet text-muted flex h-12 w-12 items-center justify-center rounded flex-shrink-0">
              <Package className="h-6 w-6" />
            </div>
          )}
          <div className="flex flex-col min-w-0 flex-1">
            <div className="text-default cursor-pointer text-base font-medium truncate">
              {product.title}
            </div>
            <div className="text-muted text-xs truncate">
              {product.slug ?? "N/A"}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="text-muted text-xs">
          {getPriceRange(product.variants)}
        </div>
      </TableCell>
      <TableCell>
        {productTypeOption ? (
          <div className="flex items-center gap-2">
            <ProductTypeIcon
              icon={productTypeOption.icon}
              color={productTypeOption.color}
            />
            <span className="text-default text-xs truncate">
              {productTypeOption.label}
            </span>
          </div>
        ) : (
          <div className="text-muted text-xs truncate">Uncategorized</div>
        )}
      </TableCell>
      <TableCell UNSAFE_className="text-right">
        <ProductMenu onEditProduct={onEditProduct} />
      </TableCell>
    </>
  );
};

const LoadingRow: React.FC<{ columnCount: number }> = ({ columnCount }) => (
  <TableRow>
    {Array.from({ length: columnCount }).map((_, index) => (
      <TableCell key={index}>
        <Skeleton className="h-12 w-full" />
      </TableCell>
    ))}
  </TableRow>
);

export function ProductsTable() {
  const currentProjectId = useCurrentProjectId();
  const navigate = useNavigate();
  const { project } = useCurrentProjectContext();
  const isMobile = useIsMobile();

  const isShopifyProject = project?.integrations?.shopify;
  const shopifyStore = isShopifyProject?.store;

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const [searchQuery, debouncedSearchQuery, setSearchQuery] = useDebouncedState(
    "",
    500,
  );

  const {
    data: semanticSearchData,
    isLoading: isLoadingSemanticSearch,
    fetchNextPage: fetchNextSemanticPage,
    isFetching: isFetchingSemantic,
    hasNextPage: hasNextSemanticPage,
  } = productRouter.find.useInfiniteQuery(
    currentProjectId && debouncedSearchQuery
      ? {
          config: {
            projectId: currentProjectId,
            filters: {
              searchTerm: debouncedSearchQuery,
              semantic: true,
            },
          },
          pageSize: PAGE_SIZE,
        }
      : skipToken,
    {
      enabled: Boolean(currentProjectId && debouncedSearchQuery),
      staleTime: 0,
      getNextPageParam: ({ nextCursor }) => nextCursor,
    },
  );

  const {
    data: listData,
    isLoading: isLoadingList,
    fetchNextPage: fetchNextListPage,
    isFetching: isFetchingList,
    hasNextPage: hasNextListPage,
  } = productRouter.list.useInfiniteQuery(
    currentProjectId && !debouncedSearchQuery
      ? {
          projectId: currentProjectId,
          // Note (Noah, 2025-09-08): This is always true for products dashboard, since if
          // the user got here they're in a v2 project and thus have updated their scopes
          writeThroughProductsSync: true,
          filters: {
            searchTerm: debouncedSearchQuery,
          },
          limit: PAGE_SIZE,
        }
      : skipToken,
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    },
  );

  const data = debouncedSearchQuery ? semanticSearchData : listData;
  const isLoadingProducts = debouncedSearchQuery
    ? isLoadingSemanticSearch
    : isLoadingList;
  const fetchNextPage = debouncedSearchQuery
    ? fetchNextSemanticPage
    : fetchNextListPage;
  const isFetching = debouncedSearchQuery ? isFetchingSemantic : isFetchingList;
  const hasNextPage = debouncedSearchQuery
    ? hasNextSemanticPage ?? false
    : hasNextListPage ?? false;

  const allProducts = React.useMemo(() => {
    return data?.pages.flatMap((page) => page.products) ?? [];
  }, [data]);

  const totalCount = React.useMemo(() => {
    if (debouncedSearchQuery) {
      return allProducts.length;
    }
    return listData?.pages[0]?.totalCount ?? 0;
  }, [debouncedSearchQuery, allProducts.length, listData]);

  const tableData = React.useMemo<ProductTableRow[]>(() => {
    const products: ProductTableRow[] = allProducts.map((product) => ({
      ...product,
      key: product.id,
      isLoading: false,
    }));

    if (isFetching && !isLoadingProducts && allProducts.length > 0) {
      products.push({
        key: "loading-row",
        isLoading: true,
      });
    }

    return products;
  }, [allProducts, isFetching, isLoadingProducts]);

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (
          scrollHeight - scrollTop - clientHeight < 100 &&
          !isFetching &&
          hasNextPage
        ) {
          void fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetching, hasNextPage],
  );

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const handleScroll = () => {
      fetchMoreOnBottomReached(container);
    };

    fetchMoreOnBottomReached(container);

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [fetchMoreOnBottomReached]);

  useAllowScroll();

  const onProductClick = React.useCallback(
    (clickedProductId: string) => {
      if (currentProjectId) {
        const path = generatePath(routes.shops.products.edit, {
          projectId: currentProjectId,
          productId: clickedProductId,
        });
        navigate(path);
      }
    },
    [currentProjectId, navigate],
  );

  const onEditInShopify = React.useCallback(() => {
    if (shopifyStore?.shopifyUrl) {
      openShopifyProductPageUrl(shopifyStore.shopifyUrl);
    }
  }, [shopifyStore?.shopifyUrl]);

  const onNewProduct = React.useCallback(() => {
    navigate(
      generatePath(routes.shops.products.new, {
        projectId: currentProjectId,
      }),
    );
  }, [currentProjectId, navigate]);

  const headerDescription = React.useMemo(() => {
    if (isLoadingProducts) {
      return <Skeleton className="h-4 w-48" />;
    }

    if (isShopifyProject) {
      return `${totalCount} ${totalCount === 1 ? "product" : "products"} synced from Shopify`;
    }

    return `${totalCount} Replo ${totalCount === 1 ? "product" : "products"}`;
  }, [isLoadingProducts, isShopifyProject, totalCount]);

  const columnCount = isShopifyProject ? 6 : 4;

  const emptyState = (
    <Empty
      icon={<Package />}
      title="No products found"
      description={
        searchQuery ? "Try adjusting your search." : "No products available."
      }
    >
      {isShopifyProject ? (
        <Button
          variant="secondary"
          size="sm"
          endEnhancer={<ExternalLink />}
          onClick={onEditInShopify}
        >
          Edit in Shopify
        </Button>
      ) : (
        <Button
          variant="primary"
          size="sm"
          startEnhancer={<Plus />}
          onClick={onNewProduct}
        >
          New Product
        </Button>
      )}
    </Empty>
  );

  return (
    <>
      <PageHeader
        title="Products"
        description={headerDescription}
        actions={
          <div className="flex gap-2">
            {isShopifyProject ? (
              <Button
                variant="secondary"
                size="base"
                endEnhancer={<ExternalLink />}
                onClick={onEditInShopify}
              >
                Edit in Shopify
              </Button>
            ) : (
              <Button
                variant="primary"
                size="base"
                startEnhancer={<Plus />}
                onClick={onNewProduct}
              >
                New Product
              </Button>
            )}
          </div>
        }
      />
      <div
        className={twMerge(
          "flex w-full flex-col gap-y-6 py-6",
          isMobile && "px-4",
        )}
      >
        <Input
          placeholder="Search products..."
          startEnhancer={<Search />}
          className="w-full"
          size="base"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div
          ref={scrollContainerRef}
          className="border border-border border-0.5 rounded-xl overflow-y-auto"
          style={{
            maxHeight: `calc(100vh - ${isMobile ? "285px" : "260px"})`,
          }}
        >
          <Table>
            <TableHeader>
              <TableRow UNSAFE_className="bg-sheet hover:bg-sheet">
                {isShopifyProject ? (
                  <>
                    <TableHead>Product</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Variants</TableHead>
                    <TableHead>Options</TableHead>
                  </>
                ) : (
                  <>
                    <TableHead>Name</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>{null}</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingProducts &&
                Array.from({ length: 20 }).map((_, index) => (
                  <LoadingRow key={index} columnCount={columnCount} />
                ))}
              {!isLoadingProducts && tableData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount}>
                    <div className="text-default [&_.border-border]:border-border">
                      {emptyState}
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!isLoadingProducts &&
                tableData.length > 0 &&
                tableData.map((row) => {
                  if (row.isLoading) {
                    return (
                      <LoadingRow key={row.key} columnCount={columnCount} />
                    );
                  }

                  const isClickable = !isShopifyProject;

                  return (
                    <TableRow
                      key={row.key}
                      onClick={
                        isClickable ? () => onProductClick(row.id) : undefined
                      }
                      UNSAFE_className={
                        isClickable ? "cursor-pointer" : "cursor-default"
                      }
                    >
                      {isShopifyProject ? (
                        <ShopifyProductCells product={row} />
                      ) : (
                        <ReploProductCells
                          product={row}
                          onEditProduct={() => onProductClick(row.id)}
                        />
                      )}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}
